import { Component } from '@theme/component';
import { isLowPowerDevice, requestIdleCallback } from '@theme/utilities';

/**
 * Desktop series preview. The stage rests on the whole range; hovering or focusing a series
 * name previews that series (photo, readout, line and button) and leaving the names returns
 * to rest. The names are links, so a click or a tap simply opens the series. Touch devices get
 * no preview, and this element does no work below 750px.
 *
 * Slide 0 is the rest image (the LCP). Series slides carry data-srcset and are fetched at low
 * priority once LCP has fired, so hover feels instant without costing paint.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} slides
 * @property {HTMLAnchorElement[]} names
 * @property {HTMLElement} activeLabel
 * @property {HTMLElement} activeCount
 * @property {HTMLElement} activePrice
 * @property {HTMLElement} activeLine
 * @property {HTMLAnchorElement} cta
 *
 * @extends {Component<Refs>}
 */
class DtHeroComponent extends Component {
  requiredRefs = ['activeLabel', 'activeCount', 'activePrice', 'activeLine', 'cta'];

  /** @type {number | null} */
  #preview = null;
  #canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Weak device or Save-Data: keep the preview, skip the blur veil and the prefetch.
    if (isLowPowerDevice() || navigator.connection?.saveData) {
      this.dataset.light = '';
      return;
    }
    if (!this.#canHover.matches) return;

    const { signal } = this.#controller;
    const prefetch = () => requestIdleCallback(() => this.refs.slides?.forEach((img) => this.#load(img, 'low')));
    if (PerformanceObserver.supportedEntryTypes?.includes('largest-contentful-paint')) {
      const observer = new PerformanceObserver(() => {
        observer.disconnect();
        prefetch();
      });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    } else {
      window.addEventListener('load', prefetch, { once: true, signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** on:pointerenter / on:focus="/enter/{{ index }}" */
  enter(index) {
    if (!this.#canHover.matches) return;
    this.#preview = Number(index);
    this.#load(this.refs.slides?.[this.#preview + 1], 'high');
    this.#render();
  }

  /** on:pointerleave / on:blur="/leave" */
  leave() {
    if (this.#preview === null) return;
    this.#preview = null;
    this.#render();
  }

  /** @param {HTMLElement | undefined} img @param {'high' | 'low'} priority */
  #load(img, priority) {
    if (!(img instanceof HTMLImageElement) || img.srcset || !img.dataset.srcset) return;
    img.fetchPriority = priority;
    img.sizes = '100vw';
    img.srcset = img.dataset.srcset;
  }

  #render() {
    const index = this.#preview;
    const name = index === null ? null : this.refs.names?.[index];
    this.toggleAttribute('data-previewing', Boolean(name));
    this.style.setProperty('--veil-delay', name ? '150ms' : '800ms');

    const slide = name ? index + 1 : 0;
    this.refs.slides?.forEach((el, i) => el.classList.toggle('is-active', i === slide));
    this.refs.names?.forEach((el, i) => el.classList.toggle('is-active', i === index));

    // At rest the readout describes the whole range: just the lowest price.
    this.refs.activeLabel.textContent = name?.dataset.label ?? '';
    this.refs.activeCount.textContent = name?.dataset.count ?? '';
    this.refs.activePrice.textContent = name ? (name.dataset.price ?? '') : (this.dataset.from ?? '');
    this.refs.activeLine.textContent = name?.dataset.line ?? '';
    this.refs.cta.textContent = name?.dataset.cta ?? this.dataset.ctaLabel ?? '';
    this.refs.cta.href = name?.getAttribute('href') ?? this.dataset.ctaHref ?? '';
  }
}

if (!customElements.get('dt-hero-component')) {
  customElements.define('dt-hero-component', DtHeroComponent);
}
